<script lang="ts">
  import { onMount } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { mdiContentSave, mdiRestore } from "@mdi/js"
  import AvailabilityPolicyEditor from "@kontextwerk/control-web-frame/components/availabilityPolicies/AvailabilityPolicyEditor.svelte"
  import {
    normalizeAvailabilityPolicyConfig,
    validateAvailabilityPolicyConfig,
  } from "@kontextwerk/control-web-frame/logic/availabilityPolicies"
  import { updateAvailabilityPolicy } from "@kontextwerk/control-web-frame/api/availabilityPolicies"
  import type {
    AvailabilityPolicy,
    AvailabilityPolicyConfig,
    AvailabilityPolicySurfaceTone,
  } from "@kontextwerk/control-web-frame/types/availabilityPolicies"
  import type { OrganizationUser } from "@kontextwerk/control-web-client"
  import UserTabState from "@kontextwerk/control-web-frame/routes/OrgaView/subviews/User/components/UserTabState.svelte"
  import { fetchUserSettings } from "../../../../../api/kehrblatt/user-settings"

  type ExtendedUser = OrganizationUser & {
    availability_policy_id?: string | null
    availability_policy?: AvailabilityPolicy | null
  }

  const {
    user = null,
    organizationId = null,
    loading = false,
    error = null,
    onRetry = null,
    surfaceTone = "light",
    onUpdated = () => {},
  }: {
    user?: ExtendedUser | null
    organizationId?: string | null
    loading?: boolean
    error?: string | null
    onRetry?: (() => void) | null
    surfaceTone?: AvailabilityPolicySurfaceTone
    onUpdated?: (user: ExtendedUser) => void
  } = $props()

  let policy = $state<AvailabilityPolicy | null>(null)
  let draft = $state<AvailabilityPolicyConfig | null>(null)
  let initialDraft = $state<AvailabilityPolicyConfig | null>(null)
  let settingsLoading = $state(false)
  let saving = $state(false)
  let formError = $state<string | null>(null)
  let loadedUserId = $state<string | null>(null)
  let mounted = false

  const applyTargetPolicy = (userId: string, nextPolicy: AvailabilityPolicy | null) => {
    policy = nextPolicy
    draft = normalizeAvailabilityPolicyConfig(nextPolicy)
    initialDraft = normalizeAvailabilityPolicyConfig(nextPolicy)
    formError = null
    loadedUserId = userId
  }

  const clearTarget = () => {
    policy = null
    draft = null
    initialDraft = null
    settingsLoading = false
    formError = null
    loadedUserId = null
  }

  const loadSettings = async (userId: string) => {
    try {
      const settings = await fetchUserSettings(userId)

      if (!mounted || user?.id !== userId) return

      applyTargetPolicy(userId, settings.availability_policy ?? null)
    } catch (err) {
      if (!mounted || user?.id !== userId) return

      console.error("availability_policy_load_failed", err)
      formError = "Arbeitszeiten konnten nicht geladen werden."
    } finally {
      if (mounted && user?.id === userId) {
        settingsLoading = false
      }
    }
  }

  onMount(() => {
    mounted = true
    const targetUser = user
    const targetUserId = targetUser?.id ?? null

    if (!targetUserId) {
      clearTarget()
    } else {
      applyTargetPolicy(targetUserId, targetUser?.availability_policy ?? null)
      settingsLoading = true
      void loadSettings(targetUserId)
    }

    return () => {
      mounted = false
    }
  })

  const dirty = $derived.by(() => JSON.stringify(draft) !== JSON.stringify(initialDraft))
  const busy = $derived(loading || settingsLoading || saving)
  const targetReady = $derived(Boolean(user?.id && loadedUserId === user.id && policy?.id))
  const controlDarkMode = $derived(surfaceTone === "dark")

  const handleSave = async () => {
    const targetUser = user
    const targetUserId = targetUser?.id ?? null
    const targetPolicy = policy
    const targetPolicyId = targetPolicy?.id ?? null

    if (
      !mounted ||
      !targetUser ||
      !targetUserId ||
      loadedUserId !== targetUserId ||
      !organizationId ||
      !targetPolicy ||
      !targetPolicyId
    ) {
      newNotification({ class: "error", html: "Bitte zuerst Benutzer und Organisation speichern." })

      return
    }

    const normalized = normalizeAvailabilityPolicyConfig(draft)
    const validationError = validateAvailabilityPolicyConfig(normalized)

    if (validationError) {
      formError = validationError
      newNotification({ class: "error", html: validationError })

      return
    }

    saving = true
    try {
      const savedPolicy = await updateAvailabilityPolicy(organizationId, targetPolicyId, {
        name: targetPolicy.name,
        ...normalized,
      })

      if (
        !mounted ||
        user?.id !== targetUserId ||
        loadedUserId !== targetUserId ||
        policy?.id !== targetPolicyId
      ) {
        return
      }

      policy = savedPolicy
      draft = normalizeAvailabilityPolicyConfig(savedPolicy)
      initialDraft = normalizeAvailabilityPolicyConfig(savedPolicy)
      formError = null
      onUpdated({
        ...targetUser,
        availability_policy_id: savedPolicy.id,
        availability_policy: savedPolicy,
      } as ExtendedUser)
      newNotification({ class: "success", html: "Arbeitszeiten gespeichert." })
    } catch (err) {
      if (!mounted || user?.id !== targetUserId || loadedUserId !== targetUserId) return

      console.error("availability_policy_save_failed", err)
      formError = "Arbeitszeiten konnten nicht gespeichert werden."
      newNotification({ class: "error", html: "Arbeitszeiten konnten nicht gespeichert werden." })
    } finally {
      if (mounted) saving = false
    }
  }

  const handleReset = () => {
    draft = normalizeAvailabilityPolicyConfig(initialDraft)
    formError = null
  }
</script>

{#if loading || settingsLoading || error || !user || loadedUserId !== user.id}
  <UserTabState
    loading={loading || settingsLoading || Boolean(user && loadedUserId !== user.id)}
    {error}
    userPresent={Boolean(user)}
    {onRetry}
  />
{:else}
  <section class="working-hours form">
    <AvailabilityPolicyEditor
      value={draft}
      {organizationId}
      label="Arbeitszeiten"
      disabled={busy}
      errorText={formError}
      {surfaceTone}
      onChange={(nextValue) => {
        draft = nextValue
        formError = null
      }}
    >
      {#snippet buttonSnippet()}
        <CTA
          type="primaryButton"
          icon={mdiContentSave}
          content={saving ? "Speichere…" : "Speichern"}
          onClick={handleSave}
          disabled={busy || !dirty || !targetReady}
          darkMode={controlDarkMode}
          options={{
            label: "Arbeitszeitoptionen",
            disabled: busy,
            items: [
              {
                id: "discard",
                label: "Änderungen verwerfen",
                icon: mdiRestore,
                disabled: busy || !dirty,
                onClick: handleReset,
              },
            ],
          }}
        />
      {/snippet}
    </AvailabilityPolicyEditor>
  </section>
{/if}

<style lang="less">
  .working-hours {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
</style>
